import { useEffect, useState } from 'react';

/**
 * Returns a debounced version of the provided value.
 *
 * The debounced value only updates after `delay` milliseconds have passed
 * since the last change to `value`. Useful for delaying API calls triggered
 * by rapid user input (e.g., search-as-you-type).
 *
 * @param value  The raw/instant value to debounce.
 * @param delay  Milliseconds to wait before updating the debounced value (default 300).
 */
export function useDebounce<T>(value: T, delay: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}
